import {
  every,
  get,
  has,
  isArray,
  isEmpty,
  isEqual,
  isFunction,
  isNull,
  isNumber,
  isPlainObject,
  isString,
  map,
  mapKeys,
  mapValues,
} from "lodash";
import { createI18nMessage, helpers } from "@vuelidate/validators";
import * as vuelidateValidators from "@vuelidate/validators";
import * as customValidators from "@/helpers/customValidators";

const validators: any = { ...vuelidateValidators, ...customValidators };

import messages from "@/assets/lang/es/validations.json";

export const buildDataFromSchema = ({ schema, context }: {
  schema: Record<string, any>,
  context: Record<string, any>
}): any => {
  const source = get(schema, "source", null);
  if (source) {
    if (typeof source === "string") {
      return getDataFromContext({ path: source, context: context });
    } else if (isArray(source)) {
      return map(source, (item: string) => getDataFromContext({ path: item, context: context }));
    } else if (typeof source === "object") {
      return "";  // TODO: Implementar ejecución de drivers
    }
  } else {
    if (schema["type"] === "object") {
      const data = mapValues(schema.properties, (value, _) =>
        buildDataFromSchema({ schema: value, context }),
      );

      return isEmpty(data) ? get(schema, "default", {}) : data;
    } else if (schema["type"] === "array") {
      const data = buildDataFromSchema({ schema: get(schema, "items", {}), context: context });

      return isEmpty(data) ? get(schema, "default", []) : data;
    }

    return get(schema, "default", null);
  }
};

export const getDataFromContext = ({ path, context }: { path: string; context: any }): any => {
  const [key, ...rest] = path.match(/(?:[^.(]|\([^)]*\))+/g) ?? [];
  const remainder = rest.join(".");

  const predicate = key?.match(/^(.+?)\(([^:]+):(.+)\)$/);

  let data: any;
  if (predicate) {
    const [, arrayKey, attribute, value] = predicate;
    const collection = get(context, arrayKey, null);
    data = isArray(collection)
      ? collection.find((item: any) => String(get(item, attribute)) === value) ?? null
      : null;
  } else {
    data = get(context, key, null);
  }

  if (isNull(data)) {
    return data;
  }

  if (remainder) {
    return getDataFromContext({ path: remainder, context: data });
  }

  return data;
};

export const resolvePathTokens = ({ path, context }: { path: string; context: any }): string => {
  return path.replace(/\[([^\]]+)\]/g, (_, tokenPath) => {
    const value = getDataFromContext({
      path: tokenPath,
      context,
    });

    if (value === null || value === undefined) {
      return _;
    }

    return String(value);
  });
};

async function i18nImport() {
  if (process.env.NODE_ENV === "development") {
    //@ts-ignore
    return await import("vue-i18n/dist/vue-i18n.esm-bundler.js");
  } else {
    //@ts-ignore
    return await import("vue-i18n/dist/vue-i18n.runtime.esm-bundler.js");
  }
}

export const validationRules = async (validations: any) => {
  const i18nLib = await i18nImport();
  const i18n = i18nLib.createI18n({ locale: "es", messages });
  const withI18nMessage = createI18nMessage({ t: i18n.global.t.bind(i18n) });

  const rules: any = {};

  for (const validation of validations) {
    let validator = validators[validation.type];

    if (!validator) {
      continue;
    }

    if (has(validation, "params")) {
      validator = validator(validation.params);
    }

    if (has(validation, "message")) {
      rules[validation.type] = helpers.withMessage(validation.message, validator);
    } else if (isFunction(validator.$message)) {
      rules[validation.type] = validator;
    } else {
      rules[validation.type] = withI18nMessage(validator, { withArguments: true });
    }
  }

  return rules;
};

const normalize = (value: any) => {
  if (isNumber(value) || isString(value)) {
    return String(value);
  }

  return value;
};

const conditions: Record<string, Function> = {
  and: (...args: any[]): boolean => {
    return args.every((arg: any) => (arg?.condition ? resolveCondition(arg.condition, arg.values) : arg));
  },
  equal: (...args: any[]): boolean => {
    if (args.length <= 1) {
      return true;
    }

    const base: any = normalize(args[0]);

    return every(args, (arg) => isEqual(normalize(arg), base));
  },
  gt: (arg1: number, arg2: number): boolean => {
    return arg1 > arg2;
  },
  gte: (arg1: number, arg2: number): boolean => {
    return arg1 >= arg2;
  },
  in: (arg1: any, arg2: any[]): boolean => {
    if (arg2) {
      if (typeof arg1 === "object" && !Array.isArray(arg1)) {
        for (const item of arg2) {
          if (isEqual(item, arg1)) {
            return true;
          }
        }
        return false;
      } else if (typeof arg1 === "string") {
        return arg2.includes(arg1);
      }
    }

    return false;
  },
  notIn: (arg1: any, arg2: any[]): boolean => {
    return !conditions.in(arg1, arg2);
  },
  lt: (arg1: number, arg2: number): boolean => {
    return arg1 < arg2;
  },
  lte: (arg1: number, arg2: number): boolean => {
    return arg1 <= arg2;
  },
  notEqual: (...args: any[]): boolean => {
    return new Set(args).size === args.length;
  },
  or: (...args: any[]): boolean => {
    return args.some((arg: any) => (arg?.condition ? resolveCondition(arg.condition, arg.values) : arg));
  },
  notNull: (...args: any[]): boolean => {
    return args.every((arg: any) => arg !== null);
  },
  notEmpty: (...args: any[]): boolean => {
    return args.every((arg: any) => !isEmpty(arg));
  },
};

export const resolveCondition: any = (condition: string, values: any[]) => {
  return conditions[condition](...values);
};

export const resolveValues = (values: any[] = [], context: any = {}): any  => {
  return values.map((value) => {
    if (isArray(value)) {
      return resolveValues(value, context);
    }

    if (isString(value) && value.startsWith("$")) {
      return getDataFromContext({
        path: resolvePathTokens({
          path: resolveContextPath(value, context),
          context,
        }),
        context: context,
      });
    }

    return value;
  });
};

export const resolveContextPath = (eventName: string, context: any): string  => {
  return eventName.replace(/{{(.*?)}}/g, (_, token) => {
    const value: any = get(context, token.trim());
    return value !== null ? String(value) : "";
  });
};

export const resolveObjectEventNames = (obj: any, context: any): any  => {
  if (isArray(obj)) {
    return obj.map(item => resolveObjectEventNames(item, context));
  }

  if (isPlainObject(obj)) {
    return mapKeys(
      mapValues(obj, value => resolveObjectEventNames(value, context)),
      (_: any, key: string): string => resolveContextPath(key, context),
    );
  }

  return obj;
};